import React, { useState, useEffect, useMemo } from "react";
import {
    useTheme,
    TableSortLabel,
    InputAdornment,
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Paper,
    Pagination,
    IconButton,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Autocomplete,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid
} from "@mui/material";
import { Helmet } from "react-helmet";
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { useUser } from "../context/UserContext";
import SectionLoader from "../components/SectionLoader";
import Toast from "../components/Toast";

// StatCard Component for displaying statistics
function StatCard({ label, count, IconComponent, color = '#177373' }) {
    return (
        <Card variant="outlined" sx={{ height: "100%", minWidth: { xs: '100%', sm: 200 }, width: "100%", borderRadius: 2 }}>
            <CardContent sx={{ minHeight: 80 }}>
                <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                    <Box
                        sx={{
                            height: 48,
                            width: 48,
                            backgroundColor: 'grey.200',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            flexShrink: 0,
                        }}
                    >
                        <IconComponent sx={{ fontSize: 28, color: color }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h5" fontWeight="bold" noWrap>
                            {count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {label}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function InactiveUsers() {
    const muiTheme = useTheme();
    const isLight = muiTheme.palette.mode === 'light';
    const [sortOrder, setSortOrder] = useState('asc');
    // API Configuration
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/admin/';

    // User context
    const { user } = useUser();

    // State for users list
    const [users, setUsers] = useState([]);
    const [selectedUserForView, setSelectedUserForView] = useState(null);
    const [userDetailModalOpen, setUserDetailModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    // Filter states
    const [daysInactive, setDaysInactive] = useState(30);
    const [searchQuery, setSearchQuery] = useState("");
    const [organization, setOrganization] = useState(null);
    const [organizations, setOrganizations] = useState([]);

    // Statistics
    const [stats, setStats] = useState({
        total_inactive: 0,
        inactive_1_to_2_months: 0,
        inactive_2_plus_months: 0
    });

    // Selection state
    const [selectedUsers, setSelectedUsers] = useState([]);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const itemsPerPage = 20;

    // Toast notification
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    // Fetch organizations (for super admin)
    useEffect(() => {
        if (user?.role === 1) {
            fetchOrganizations();
        }
    }, [user]);

    // Fetch data when filters change
    useEffect(() => {
        fetchInactiveUsers();
        fetchStatistics();
    }, [organization, daysInactive, page]);

    const fetchOrganizations = async () => {
        try {
            const response = await fetch(`${API_URL}roles-organizations`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to fetch organizations', severity: "error" });
            } else {
                setOrganizations(data.organizations || []);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const fetchInactiveUsers = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                days_inactive: daysInactive,
                page: page,
                limit: itemsPerPage
            });

            if (organization) {
                params.append('org_id', organization.id);
            }

            const response = await fetch(`${API_URL}get-inactive-users?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to fetch inactive users', severity: "error" });
            } else {
                setUsers(data.data || []);
                setTotalPages(data.pagination?.total_pages || 1);
                setTotalCount(data.pagination?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching inactive users:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const params = new URLSearchParams();
            if (organization) {
                params.append('org_id', organization.id);
            }

            const response = await fetch(`${API_URL}inactive-user-stats?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await response.json();

            if (data.success) {
                setStats(data.data);
            }
        } catch (error) {
            console.error('Error fetching statistics:', error);
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedUsers(users.map(u => u.id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const handleSendReminders = async () => {
        if (selectedUsers.length === 0) {
            setToast({ open: true, message: 'Please select at least one user', severity: 'warning' });
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to send reminder emails to ${selectedUsers.length} user(s)? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            setSending(true);
            const response = await fetch(`${API_URL}send-inactive-user-reminders`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_ids: selectedUsers }),
            });

            const data = await response.json();

            if (data.success) {
                setToast({
                    open: true,
                    message: `Successfully sent ${data.emails_sent} reminder(s)!`,
                    severity: 'success'
                });
                setSelectedUsers([]);
                fetchInactiveUsers();
            } else {
                setToast({ open: true, message: data.message || 'Failed to send reminders', severity: 'error' });
            }
        } catch (error) {
            console.error('Error sending reminders:', error);
            setToast({ open: true, message: 'Network error. Please try again.', severity: 'error' });
        } finally {
            setSending(false);
        }
    };

    const handleRefresh = () => {
        setPage(1);
        setSelectedUsers([]);
        fetchInactiveUsers();
        fetchStatistics();
    };

    // Filter users by search query
    const filteredUsers = users.filter(user => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        return fullName.includes(query) || email.includes(query);
    });

    return (
        <>
            <Helmet>
                <title>Inactive Users | GPN Admin</title>
            </Helmet>

            <Box sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" fontWeight="bold">
                        Inactive Users
                    </Typography>
                    <IconButton onClick={handleRefresh} color="primary">
                        <RefreshIcon />
                    </IconButton>
                </Box>

                                {/* Compact Unified Toolbar */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        mb: 2.5,
                        borderRadius: 2.5,
                        background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.04)",
                        border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.08)",
                        backdropFilter: "blur(20px)",
                    }}
                >
                    {/* Row 1: Title, Count, Search, Sort & Actions */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                            pb: 1.25,
                            borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255,255,255,0.06)",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                                Inactive Users
                            </Typography>
                            <Chip
                                label={`${stats.total_inactive || filteredUsers.length} Total`}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    bgcolor: "rgba(239, 68, 68, 0.12)",
                                    color: "#EF4444",
                                }}
                            />
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <TextField
                                size="small"
                                placeholder="Search by name or email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: { xs: "100%", sm: 220 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
                            />

                            <Select
                                size="small"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                displayEmpty
                                renderValue={(val) => `Sort: ${val === 'asc' ? 'A → Z' : val === 'desc' ? 'Z → A' : 'Newest'}`}
                                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
                            >
                                <MenuItem value="asc">Name: A → Z (Alphabetical)</MenuItem>
                                <MenuItem value="desc">Name: Z → A</MenuItem>
                                <MenuItem value="newest">Date: Newest First</MenuItem>
                            </Select>

                            <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                startIcon={<EmailIcon />}
                                onClick={handleSendReminders}
                                disabled={selectedUsers.length === 0 || sending}
                                sx={{ textTransform: "none", fontSize: "0.8rem", py: 0.6 }}
                            >
                                {sending ? 'Sending...' : `Remind (${selectedUsers.length})`}
                            </Button>
                        </Box>
                    </Box>

                    {/* Row 2: Inactivity Triage Chips & Secondary Filters */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.25,
                            pt: 1.25,
                        }}
                    >
                        {/* Chips */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            {[
                                { days: 30, label: "Total Inactive", count: stats.total_inactive || 0 },
                                { days: 60, label: "1-2 Months", count: stats.inactive_1_to_2_months || 0 },
                                { days: 90, label: "2+ Months", count: stats.inactive_2_plus_months || 0 },
                            ].map((tab) => {
                                const isActive = daysInactive === tab.days;
                                return (
                                    <Chip
                                        key={tab.days}
                                        label={`${tab.label} (${tab.count})`}
                                        size="small"
                                        onClick={() => {
                                            setDaysInactive(tab.days);
                                            setPage(1);
                                        }}
                                        sx={{
                                            cursor: "pointer",
                                            fontWeight: isActive ? 700 : 500,
                                            fontSize: "0.78rem",
                                            height: 26,
                                            bgcolor: isActive
                                                ? "#EF4444"
                                                : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.06)"),
                                            color: isActive ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.7)"),
                                            "&:hover": {
                                                bgcolor: isActive ? "#DC2626" : (isLight ? "#E2E8F0" : "rgba(255,255,255,0.12)"),
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>

                        {/* Org Filter & Refresh */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            {user?.role === 1 && (
                                <Autocomplete
                                    size="small"
                                    options={organizations}
                                    getOptionLabel={(option) => option.org_name || ""}
                                    value={organization}
                                    onChange={(event, newValue) => {
                                        setOrganization(newValue);
                                        setPage(1);
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Organization" size="small" />
                                    )}
                                    sx={{ width: 170, "& .MuiInputBase-input": { py: "2px !important", fontSize: "0.8rem" } }}
                                />
                            )}
                            <IconButton size="small" onClick={handleRefresh} color="primary">
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </Paper>

                {/* Users Table */}
                {loading ? (
                    <SectionLoader />
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                            indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={true}
                                            direction={sortOrder}
                                            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                        >
                                            <strong>Name</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell><strong>Email</strong></TableCell>
                                    <TableCell><strong>Organization</strong></TableCell>
                                    <TableCell><strong>Last Used</strong></TableCell>
                                    <TableCell><strong>Days Inactive</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="body2" color="text.secondary" py={3}>
                                                No inactive users found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <TableRow
                                            key={user.id}
                                            hover
                                            onClick={() => {
                                                setSelectedUserForView(user);
                                                setUserDetailModalOpen(true);
                                            }}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: isLight ? 'rgba(13, 148, 136, 0.06) !important' : 'rgba(76, 161, 175, 0.12) !important' }
                                            }}
                                        >
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedUsers.includes(user.id)}
                                                    onChange={() => handleSelectUser(user.id)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {user.first_name} {user.last_name}
                                            </TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>{user.origanisation?.org_name || 'N/A'}</TableCell>
                                            <TableCell>{user.last_used_formatted}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${user.days_inactive} days`}
                                                    color={user.days_inactive > 60 ? 'error' : 'warning'}
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                )}

                {/* Summary */}
                <Box mt={2} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                        Showing {filteredUsers.length} of {totalCount} inactive users
                        {selectedUsers.length > 0 && ` • ${selectedUsers.length} selected`}
                    </Typography>
                </Box>
            </Box>

            {/* Inactive User Detail Modal */}
            {userDetailModalOpen && selectedUserForView && (
                <Dialog
                    open={userDetailModalOpen}
                    onClose={() => {
                        setUserDetailModalOpen(false);
                        setSelectedUserForView(null);
                    }}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: "16px",
                            bgcolor: isLight ? "#FFFFFF" : "#1e293b",
                            color: isLight ? "#0F172A" : "#FFFFFF",
                            boxShadow: isLight ? "0 20px 40px rgba(0,0,0,0.15)" : "0 20px 40px rgba(0,0,0,0.5)",
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.1)",
                            pb: 1.5,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <PersonOffIcon sx={{ color: "#EF4444" }} />
                            <Typography variant="h6" fontWeight={700}>
                                Inactive User Details
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setUserDetailModalOpen(false);
                                setSelectedUserForView(null);
                            }}
                            sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)" }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                            {/* Header User Profile */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Avatar
                                    src={selectedUserForView.profile_pic || undefined}
                                    alt={selectedUserForView.first_name}
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: "14px",
                                        bgcolor: isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.2)",
                                        color: "#EF4444",
                                        border: "2px solid rgba(239, 68, 68, 0.3)",
                                        fontWeight: 700,
                                        fontSize: "1.4rem",
                                    }}
                                >
                                    {selectedUserForView.first_name?.[0]?.toUpperCase() || "?"}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight={700}>
                                        {selectedUserForView.first_name} {selectedUserForView.last_name}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                        <Chip
                                            label={`${selectedUserForView.days_inactive} days inactive`}
                                            color={selectedUserForView.days_inactive > 60 ? "error" : "warning"}
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Details Paper */}
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: "12px",
                                    bgcolor: isLight ? "#F8FAFC" : "rgba(255,255,255,0.03)",
                                    borderColor: isLight ? "#E2E8F0" : "rgba(255,255,255,0.08)",
                                }}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                                            Email Address
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {selectedUserForView.email || "N/A"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                                            Organization
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {selectedUserForView.origanisation?.org_name || "N/A"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                                            Last Active Date
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {selectedUserForView.last_used_formatted || "N/A"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                                            Days Inactive
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: "#EF4444" }}>
                                            {selectedUserForView.days_inactive} days
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
                            <Button
                                onClick={() => {
                                    setUserDetailModalOpen(false);
                                    setSelectedUserForView(null);
                                }}
                                sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)", textTransform: "none" }}
                            >
                                Close
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>
            )}

            {/* Toast Notification */}
            <Toast toast={toast} setToast={setToast} />
        </>
    );
}
 